import type { Dict } from './ru'

export const en: Dict = {
  code: 'en',
  logo: ['LIN', 'I', 'A'],
  nav: { draw: 'Draw', journal: 'Journal', dialogs: 'Dialogs' },
  step: {
    home: '',
    pre: 'check-in',
    draw: 'drawing',
    load: '',
    analysis: 'read',
    exercise: 'practice',
    result: 'result',
    fresult: 'result',
    journal: 'journal',
    dialogs: 'dialogs',
    room: 'dialog',
    replyq: 'your move',
  },
  pacer: { in: 'inhale…', out: 'exhale — lead' },

  home: {
    title: 'What shall we draw?',
    freeTitle: 'Free drawing',
    freeDesc: 'Draw whatever you like — the AI will look and respond. You can play together with friends.',
    therapyTitle: 'Art therapy',
    therapyDesc: 'Check-in → state drawing → AI read → rounding & color practice → before/after.',
    footer: 'A reflection practice, not medical diagnostics.',
  },
  streak: {
    first: 'Your first entry starts your day streak.',
    active: (n, total) =>
      `Streak: <b>${n} ${n === 1 ? 'day' : 'days'} in a row</b> · journal entries: ${total}`,
    broken: (total) => `Journal entries: ${total}. Draw today to keep the streak going.`,
  },

  checkin: {
    title: 'How are you right now?',
    moodLow: 'heavy',
    moodName: 'State',
    moodHigh: 'wonderful',
    energyLow: 'drained',
    energyName: 'Energy',
    energyHigh: 'overflowing',
    tagsHint: 'Tap what resonates (optional):',
    feelHint: 'How do you feel?',
    feelPlaceholder: 'In your own words — or skip…',
    next: 'Next',
    tags: ['calm', 'anxious', 'tired', 'angry', 'inspired', 'empty', 'joyful', 'tense'],
  },

  draw: {
    free: [
      'Free drawing',
      'Draw whatever you like. No skill needed — the freer, the more honest.',
      'When it feels like enough — hit done. A photo of a paper drawing works too.',
    ],
    therapy: [
      "Draw what's inside",
      'Abstract, with your finger. The "worse", the more honest.',
      'Lines, blots, scribbles. Or upload a photo of a paper drawing — the Photo button.',
    ],
    reply: [
      'A drawing for the dialog',
      'Your move is a line too. Draw what resonates.',
      'Next you can attach a question — let them guess you.',
    ],
    doneTherapy: 'Done — read it',
    doneReply: 'Next',
    doneFree: 'Done — show the AI',
    photo: 'Photo',
    clear: 'Clear',
  },

  load: {
    default: 'Reading the lines…',
    free: ['The AI is looking at the drawing…', 'Reading angles and pauses…', 'Writing a response…'],
    compare: 'The AI is comparing before and after…',
  },

  analysis: {
    title: 'What the drawing shows',
    readCard: 'The read',
    questionsCard: 'Questions for yourself',
    toPractice: 'Change it → practice',
    mSharp: 'sharp turns',
    mCV: 'tempo raggedness',
    mStrokes: 'strokes',
    mSec: 'of drawing',
    photoMode: 'photo mode',
    photoModeNote: 'motor data will appear in the practice — you draw in the app there',
  },

  exercise: {
    title: 'Practice',
    steps: [
      {
        tag: 'ROUNDING',
        text: 'On each exhale — one slow arc over a sharp corner. The circle in the header sets the rhythm; the line itself will tell you if you hit it.',
      },
      {
        tag: 'COLOR',
        text: 'The color of the state you want to reach. Slow wide strokes, like a brush — on the exhale.',
      },
    ],
    focusPrefix: 'Focus: ',
    next: 'Next',
    finish: 'Finish the practice',
    done: 'Done',
    hintArc: 'the line will show if you hit the rhythm',
    hintColor: 'go slowly, like a brush',
    fbLonger: 'a bit longer — one calm arc',
    fbSlower: 'slower',
    fbSmoother: 'smoother, no corners',
    fbInRhythm: 'yes — in rhythm',
    fbGoodNext: 'good. next one — on the exhale',
    fbBrushSlower: 'slower, like a brush',
    fbBrushCalmer: 'even calmer',
    fbColorOk: 'there. the color settles',
    fbUndone: 'removed the last stroke',
    freeTag: 'FINISHING TOUCHES',
    freeText: 'Add what’s missing — any tool, color, thickness. The pacer is in the header if you want the rhythm.',
    freeCounted: 'free mode — everything counts',
    freeAdded: 'added',
  },

  result: {
    title: 'Before / After',
    before: 'before',
    after: 'after',
    finalCard: 'A look at the "after"',
    statePrefix: 'State: ',
    postCard: 'Post for the entry',
    postPlaceholder: 'A few words about this session — goes to the journal…',
    savePost: 'Save post',
    postSaved: 'Saved to journal ✓',
    askCard: 'Ask the AI',
    askPlaceholder: 'About the drawing, the state, the practice…',
    aiWriting: 'The AI is writing',
    aiFailed: "Didn't work — please try again.",
    drawOver: 'Add to drawing',
    newSession: 'New session',
    stats: 'Session metrics',
    deltaSharp: 'sharp turns after the practice',
    deltaArcs: 'arcs and strokes in rhythm',
    dlCheckin: 'Check-in at start',
    dlCheckinVal: (m, e) => `state ${m}/9 · energy ${e}/9`,
    dlSharp: 'Sharp turns',
    dlCV: 'Tempo raggedness',
    dlArcs: 'Arcs and strokes in rhythm',
  },

  fresult: {
    title: 'Done',
    responseCard: 'AI response',
    postPlaceholder: 'A caption for the drawing — goes to the journal…',
    continue: 'Continue the drawing',
    newDrawing: 'New drawing',
  },

  journal: {
    title: 'Journal',
    lead: 'A feed of your drawings and states.',
    empty: 'Nothing yet — make your first drawing.',
    kindTherapy: 'art therapy',
    kindFree: 'free drawing',
  },

  dialogs: {
    title: 'Dialogs',
    lead: 'A room for two or for a group: drawings, questions, guesses — no end.',
    empty: 'No dialogs yet — draw something and send it to a friend as a question.',
    you: 'You',
    friend: 'Friend',
    ai: 'AI',
    titleWith: (name) => `Me & ${name}`,
    titleDefault: 'Dialog',
    titleGroup: 'Group',
    unread: (n) => `${n} new`,
    streak: (n) => `${n}-day streak`,
    participants: (n) => `${n} ${n === 1 ? 'participant' : 'participants'}`,
    locked: 'entry closed',
    opened: 'entry open',
    lock: 'Close entry',
    unlock: 'Open entry',
    kindDuo: 'One person',
    kindDuoHint: 'entry closes when the second one joins',
    kindGroup: 'A group',
    kindGroupHint: 'up to 12 people, link stays open',
    rename: 'Rename',
    renameTitle: 'What to call this dialog?',
    personalLabel: 'For me',
    personalPlaceholder: 'Me & Tanya',
    personalHint: 'only you see it',
    sharedLabel: 'For everyone',
    sharedPlaceholder: 'Our family',
    sharedHint: 'seen by all participants and in link previews',
    myNameLabel: 'My name in the dialog',
    save: 'Save',
    cancel: 'Cancel',
    nameTitle: 'How should we sign you?',
    nameHint: 'Participants will see this name. "mom" or "S." is fine.',
    namePlaceholder: 'Name',
    nameGo: 'Enter the dialog',
    composerText: 'Write…',
    composerDraw: 'Drawing',
    guess: 'Answer',
    guessPlaceholder: 'Your guess…',
    replyTo: 'Reply to',
    questionOn: 'Question on the drawing',
    yourMove: 'Your move is sent',
    shareMove: 'Send the move to WhatsApp',
    shareLink: 'Share link',
    copyLink: 'Copy link',
    copied: 'Link copied ✓',
    shareQuestion: (who, q, url) => `${who ? who + ': ' : ''}${q} → ${url}`,
    shareAnswered: (url) => `I answered — your move now → ${url}`,
    shareDrawing: (url) => `Drew something for you — look and reply with a drawing → ${url}`,
    sys: {
      joined: (n) => `${n || 'Someone'} joined`,
      renamed: (n, t) => (t ? `${n || 'Someone'} named the dialog "${t}"` : `${n || 'Someone'} removed the shared name`),
      locked: (n) => `${n || 'Someone'} closed entry`,
      unlocked: (n) => `${n || 'Someone'} opened entry`,
    },
    lockedPage: 'Entry to this dialog is closed — its participants chose to keep it between them.',
    fullPage: 'This dialog has no free seats.',
    notFound: 'Dialog not found — the link may have expired.',
    goHome: 'Draw your own',
    loadMore: 'Show earlier',
    preview: 'You are viewing as a guest. To answer, tell us your name.',
    joinCta: 'Join and answer',
    truthLabel: 'What you have in mind',
    truthHint: 'only the AI sees it when judging guesses',
    truthPlaceholder: 'e.g. anxiety',
  },

  replyq: {
    title: 'Your move',
    lead: 'Attach a question to the drawing — let them guess you. Or just send it.',
    send: 'Send to dialog',
    noQuestion: 'No question',
    customPlaceholder: 'Your question…',
  },

  share: {
    card: 'Send to a friend as a question',
    presets: ["Guess what's drawn", 'Guess my current state'],
    custom: 'Your own question',
    forWhom: 'For whom',
    create: 'Create dialog and link',
    open: 'Open dialog →',
    shareCard: 'Share card',
    cardHash: '#33unicorns · liniya.org',
    cardBefore: 'before',
    cardAfter: 'after',
    ogSubtitle: 'answer in a word — then draw your own',
  },

  support: {
    title: 'If things are heavy right now',
    text: 'LINIA is a reflection practice, not crisis help. If the state will not let go, or starts to feel dangerous — talk to a real person: someone close, a doctor, or a support line in your country. That is a normal step, and it works faster than a drawing.',
  },

  errors: {
    offline: 'No connection to the server — the AI is temporarily unavailable. Showing a local response; the drawing and metrics are saved.',
    aiFallback: 'The AI did not answer — this is a local response based on the metrics.',
    threadFailed: 'Could not send. Check your connection and try again.',
  },
}
